import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import { RiArrowRightSLine } from 'react-icons/ri';
import ProgressWithLabel from './ProgressWithLabel';
import { ById, Translatable } from 'common/types';
import { getTranslationPercentageMany } from 'common/modules/translations';
import { Routes } from 'routing';
import { useTranslation } from 'services/localization';
import { useSelector } from 'react-redux';

import * as TranslationsSelectors from 'selectors/TranslationsSelectors';

interface Props {
	id: string;
	title: string;
	translatablesById: ById<Translatable>;
}

const TranslatableGroupPreview = (props: Props) => {
	const { t } = useTranslation();
	const { id, title, translatablesById } = props;

	const activeLanguage = useSelector(TranslationsSelectors.activeLanguage);

	const translatables = Object.values(translatablesById);
	const translationPercentage = getTranslationPercentageMany(translatables, activeLanguage);

	if (translatables.length === 0) return null;

	return (
		<StyledWrapper>
			<StyledLink to={`${Routes.LanguagesTab('translations')}?group=${id}`}>
				<ContentWrapper>
					<Box>
						<Typography variant="h6">{title}</Typography>
						<Typography variant="body1" color="textSecondary">
							{t('account.languages.translations.translatableItems', {
								amount: translatables.length,
								defaultValue: '{{amount}} items',
							})}
						</Typography>
						<Box sx={{ maxWidth: '100%', width: 300, mt: 2 }}>
							<ProgressWithLabel value={translationPercentage.percentage} />
						</Box>
					</Box>
					<Box>
						<RiArrowRightSLine />
					</Box>
				</ContentWrapper>
			</StyledLink>
		</StyledWrapper>
	);
};

const StyledWrapper = styled(Box)(({ theme }) => ({
	borderBottom: `1px solid ${theme.palette.border.paper}`,
}));

const StyledLink = styled(Link)(({ theme }) => ({
	display: 'block',
	'&:focus': {
		outlineColor: theme.palette.primary.main,
	},
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	padding: theme.spacing(3),
}));

export default TranslatableGroupPreview;
